import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Paper,
  AppBar,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  FirstPage,
  NavigateBefore,
  NavigateNext,
  LastPage,
  Visibility,
  Assignment,
  History
} from "@mui/icons-material";
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import { GET } from "APIclient";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import properties from "constants/ApplicationConstants";

function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading,
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0",
        backgroundColor: "#fafafa",
      }}
    >
      <Typography variant="caption" sx={{ color: "#666", fontFamily: properties.fontFamily }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: "#666", fontFamily: properties.fontFamily }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: properties?.colors?.bgcolorbluethem || "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const AssetList = () => {
  const [components, setComponents] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedComponent1, setSelectedComponent1] = useState("");
  const [selectedComponent2, setSelectedComponent2] = useState("");
  const [assetNo, setAssetNo] = useState("");
  const [assetName, setAssetName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();
  const [assetId, setAssetId] = useState("");

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        const response = await GET("it-dept/components");
        const list = response.data?.data || [];
        const mappedList = list.map((c) => ({
          id: c.componentId,
          name: c.componentName,
        }));
        setComponents(mappedList);
      } catch (err) {
        console.error("Error fetching components:", err);
      }
    };
    fetchComponents();
  }, []);



  const fetchAssets = async (currentPage = page, currentPageSize = pageSize, searchFilters = {}) => {
    try {
      setLoading(true);
      const queryParams = {
        page: currentPage,
        size: currentPageSize,
        ...searchFilters
      };

      const urlParams = new URLSearchParams();
      Object.keys(queryParams).forEach(key => {
        const value = queryParams[key];
        if (value !== null && value !== undefined && value !== '') {
          urlParams.append(key, value);
        }
      });

      const apiUrl = `it-dept/assets?${urlParams.toString()}`;

      const response = await GET(apiUrl);

      const apiResponse = response?.data;
      if (apiResponse?.statusCode === 200) {
        let data = apiResponse.data;

        if (Array.isArray(data)) {
          let formattedData = data.map((item, index) => ({
            ...item,
            id: currentPage * currentPageSize + index + 1,
          }));

          if (searchFilters.isUsed !== undefined) {
            formattedData = formattedData.filter(item => {
              const itemIsUsed = Boolean(item.isUsed);
              
              return itemIsUsed === searchFilters.isUsed;
            });
            
          }

          setAssets(formattedData);
          setTotalRows(formattedData.length);
          setTotalPages(Math.ceil(formattedData.length / currentPageSize));
        } else if (data?.paginatedData) {
          let formattedData = data.paginatedData.map((item, index) => ({
            ...item,
            id: currentPage * currentPageSize + index + 1,
          }));

          if (searchFilters.isUsed !== undefined) {
            formattedData = formattedData.filter(item => {
              const itemIsUsed = Boolean(item.isUsed);
              
              return itemIsUsed === searchFilters.isUsed;
            });
            
          }

          setAssets(formattedData);
          setTotalRows(data.totalItems || 0);
          setTotalPages(data.totalPages || 0);
        } else {
          setAssets([]);
          setTotalRows(0);
          setTotalPages(0);
        }
      } else {
        setAssets([]);
        setTotalRows(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
      setAssets([]);
      setTotalRows(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setPage(0);
    const searchFilters = buildSearchFilters();
    await fetchAssets(0, pageSize, searchFilters);
  };

  const buildSearchFilters = () => {
    const filters = {};
    const trimmedAssetId = assetId.trim();
    if (trimmedAssetId) {
      filters.assetId = trimmedAssetId;
      filters.assetsId = trimmedAssetId;
    }
    if (assetNo.trim()) filters.assetNo = assetNo.trim();
    if (assetName.trim()) filters.assetName = assetName.trim();
    if (description.trim()) filters.description = description.trim();
    if (selectedComponent1) filters.componentId = selectedComponent1;

    if (status === "assigned") {
      filters.isUsed = true;
    } else if (status === "store") {
      filters.isUsed = false;
    } else if (status === "true" || status === "false") {
      filters.status = status === "true";
    }
    return filters;
  };


  const onSearch = (e) => {
    const { value, name } = e.target;

    if (name === "assetId") {
      setAssetId(value || "");
      if (!value) {
        setPage(0);
        setAssetNo("");
        setAssetName("");
        setDescription("");
        setSelectedComponent1("");
        setStatus("");
        fetchAssets(0, pageSize, {});
      }
      return;
    }

    if (name === "assetNo") {
      setAssetNo(value || "");
      if (!value) {
        setPage(0);
        setAssetName("");
        setDescription("");
        setSelectedComponent1("");
        setStatus("");
        fetchAssets(0, pageSize, {});
      }
    } else if (name === "assetName") {
      setAssetName(value || "");
      if (!value) {
        setPage(0);
        setAssetNo("");
        setDescription("");
        setSelectedComponent1("");
        setStatus("");
        fetchAssets(0, pageSize, {});
      }
    } else if (name === "description") {
      setDescription(value || "");
      if (!value) {
        setPage(0);
        setAssetNo("");
        setAssetName("");
        setSelectedComponent1("");
        setStatus("");
        fetchAssets(0, pageSize, {});
      }
    } else if (name === "selectedComponent1") {
      setSelectedComponent1(value || "");
      setPage(0);
      if (!value) {
        setAssetNo("");
        setAssetName("");
        setDescription("");
        setStatus("");
        fetchAssets(0, pageSize, {});
      }
    } else if (name === "status") {
      setStatus(value || "");
      setPage(0);
      if (!value) {
        setAssetNo("");
        setAssetName("");
        setDescription("");
        setSelectedComponent1("");
        fetchAssets(0, pageSize, {});
      }
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchAssets(page, pageSize, buildSearchFilters());
    return () => controller.abort();
  }, [page, pageSize]);

  const columns = [
    // {
    //   field: "id",
    //   headerName: "#",
    //   width: 60,
    //   headerAlign: "left",
    //   align: "left",
    //   renderCell: (params) => (
    //     <>
    //       {params.row.id || (page * pageSize + (params.api.getRowIndexRelativeToVisibleRows(params.id) || 0) + 1)}
    //     </>
    //   ),
    // },
    {
      field: "assetsId",
      headerName: "Asset ID",
      width: 90,
      headerAlign: "left",
      align: "center",
      renderCell: (params) => (
        <>
          {params.row?.assetsId || params.row?.assetId || "-"}
        </>
      ),
    },
    {
      field: "assetNo",
      headerName: "Asset No",
      width: 120,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "componentName",
      headerName: (<>Component<br/>Name</>),
      width: 100,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <>
          {params.row?.itComponent?.componentName ||
            params.row?.component?.componentName ||
            params.row?.componentName ||
            "-"}
        </>
      ),
    },
    {
      field: "assetMainDescrip",
      headerName: (<>Asset Main <br/>Description</>),
      width: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <>
          {params.row?.assetMainDescrip || params.row?.assetName || "-"}
        </>
      ),
    },
    {
      field: "assetSubDescrip",
      headerName: "Description",
      width: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <>
          {params.row?.assetSubDescrip || params.row?.description || "-"}
        </>
      ),
    },
    {
      field: "assignedTo",
      headerName: "Assigned to",
      width: 140,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <>
          {params.row?.isUsed ? (params.row?.empName || params.row?.employeeName || "Employee") : "Store"}
        </>
      ),
    },
    {
      field: "issuedDate",
      headerName: "Issued Date",
      width: 110,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <>
          {params.row?.assignedDate ? new Date(params.row.assignedDate).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          }) : "-"}
        </>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 90,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const isAssigned = params.row?.isUsed === true;
        const statusText = isAssigned ? "Assigned" : "Store";
        const statusColor = isAssigned ? "success" : "error";

        return (
          <Chip
            label={statusText}
            color={statusColor}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Action",
      width: 140,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isAssigned = params.row?.isUsed === true;

        return [
          <GridActionsCellItem
            key={`View-${params.row.assetsId}`}
            icon={
              <Tooltip title="View Details">
                <Visibility />
              </Tooltip>
            }
            label="View"
            color="primary"
            onClick={() => navigate("/viewAsset", { state: { assetId: params?.row?.assetsId } })}
          />,

          <GridActionsCellItem
            key={`Issue-${params.row.assetsId}`}
            icon={
              <Tooltip title={isAssigned ? "Asset already assigned" : "Issue Asset"}>
                <span>
                  <PersonAddIcon />
                </span>
              </Tooltip>
            }
            label="Issue Asset"
            color="info"
            disabled={isAssigned}
            onClick={() =>
              navigate("/issueAsset", {
                state: { assetId: params?.row?.assetsId, asset: params?.row }
              })
            }
          />,

          <GridActionsCellItem
            key={`History-${params.row.assetsId}`}
            icon={
              <Tooltip title="Asset History">
                <History />
              </Tooltip>
            }
            label="Asset History"
            color="warning"
            onClick={() => navigate(`/assets/asset_history/${params?.row?.assetsId}`, { state: { assetId: params?.row?.assetsId } })}
          />,
        ];
      },

    },
  ];


  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox container pt={2} pb={2} px={1}>
              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(13, 1fr)",
                  gap: 1,
                  alignItems: "center"
                }}
              >
                <Typography
                  sx={{
                    gridColumn: "span 2",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Asset List
                </Typography>
                <FormControl variant="outlined" sx={{ m: 0, width: "15ch", gridColumn: "span" }}>
                  <InputLabel id="component1-label" sx={{ fontFamily: properties.fontFamily }}>Select Component</InputLabel>
                  <Select
                    labelId="component1-label"
                    name="selectedComponent1"
                    value={selectedComponent1}
                    onChange={onSearch}
                    label="Select Component"
                    sx={{ minWidth: 100, p: 1.5, fontFamily: properties.fontFamily }}
                  >
                    <MenuItem value=""><em>Select Component</em></MenuItem>
                    {components.map((comp) => (
                      <MenuItem key={comp.id} value={comp.id} sx={{ fontFamily: properties.fontFamily }}>{comp.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <MDInput
                  sx={{ gridColumn: "span 2" }}
                  value={assetId}
                  onChange={onSearch}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleSearch();
                  }}
                  name="assetId"
                  label="Asset ID"
                  variant="outlined"
                />
                <MDInput
                  sx={{ gridColumn: "span 2" }}
                  value={assetNo}
                  onChange={onSearch}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="assetNo"
                  label="Asset No"
                  variant="outlined"
                />
                <MDInput
                  sx={{ gridColumn: "span 2" }}
                  value={assetName}
                  onChange={onSearch}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="assetName"
                  label="Asset Main Description"
                  variant="outlined"
                />
                {/* <MDInput
                  sx={{ gridColumn: "span 2" }}
                  value={description}
                  onChange={onSearch}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="description"
                  label="Asset Description"
                  variant="outlined"
                /> */}
                <FormControl variant="outlined" sx={{ m: 0, width: "15ch", gridColumn: "span" }}>
                  <InputLabel id="status-label" sx={{ fontFamily: properties.fontFamily }}>Status</InputLabel>
                  <Select
                    labelId="status-label"
                    name="status"
                    value={status}
                    onChange={onSearch}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        handleSearch();
                      }
                    }}
                    label="Status"
                    sx={{ minWidth: 100, p: 1.5, fontFamily: properties.fontFamily }}
                  >
                    <MenuItem value=""><em>All Status</em></MenuItem>
                    <MenuItem value="assigned">Assigned</MenuItem>
                    <MenuItem value="store">Store</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{
                    gridColumn: "span 2",
                    fontFamily: properties.fontFamily,
                    background: properties.colors.bgcolorbluethem
                  }}
                  variant="contained"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>

        <MDBox sx={{ overflow: "auto" }}>
          <Paper>
            <DataGrid
              rows={assets}
              columns={columns}
              paginationMode="server"
              rowCount={totalRows}
              pageSizeOptions={[20]}
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(model) => {
                if (typeof model.page === "number" && model.page !== page) {
                  handlePageChange(model.page);
                }
              }}
              loading={loading}
              getRowId={(row) => row.assetsId}
              hideFooter
              sx={{
                height: "450px",
                fontFamily: properties.fontFamily,
                fontSize: "14px",

                fontWeight: 400,
                "& .MuiDataGrid-scrollbar": { width: "10px !important" }
              }}
            />
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalRows}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={loading}
            />
          </Paper>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
};

export default AssetList;

